import React, { useState, useRef, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';
import { format, isWithinInterval, differenceInMinutes, endOfDay } from 'date-fns';
import CustomDatePicker from '../components/common/CustomDatePicker';

const { 
  FiSave, FiKey, FiUpload, FiTrash2, FiHardDrive, 
  FiDownload, FiRefreshCw, FiUserPlus, FiUsers, FiActivity, 
  FiLogOut, FiX, FiPieChart, FiClock, FiTrendingUp,
  FiCloud, FiSettings, FiToggleLeft, FiToggleRight, FiCalendar,
  FiTerminal, FiCheckCircle, FiAlertCircle, FiInfo, FiDatabase
} = FiIcons;

function Settings() {
  const { 
    data, importSalesData, importCustomerData, restoreBackup,
    clearStorage, exportDatabase, updateUsers, currentUser, logout,
    saveToCloud, loadFromCloud, loading, getLastCloudBackupTime,
    lexwareApiConfig, setApiConfig, syncLexwareInvoices,
    syncLogs, clearSyncLogs
  } = useLexware();

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSyncDebugOpen, setIsSyncDebugOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', initials: '' });
  const [isEvaluationOpen, setIsEvaluationOpen] = useState(false);
  const [evalRange, setEvalRange] = useState({ from: new Date(), to: new Date() });

  // Database UI State
  const [isDbModalOpen, setIsDbModalOpen] = useState(false);
  const [dbConfig, setDbConfig] = useState(() => {
    const saved = localStorage.getItem('grt_crm_db_config');
    if (saved) return JSON.parse(saved);
    return {
      container: 'crm-postgresql18',
      dbName: 'crm',
      username: 'postgres',
      password: 'TrBeTEcnDG9JqMhnOufh',
      url: 'postgresql://postgres:TrBeTEcnDG9JqMhnOufh@crm-postgresql18-btcdhn:5432/crm'
    };
  });

  // API Local State for Editing
  const [apiEdit, setApiEdit] = useState(lexwareApiConfig);

  const backupInputRef = useRef(null);
  const customerInputRef = useRef(null);
  const salesInputRef = useRef(null);
  const logEndRef = useRef(null);
  const isAdmin = currentUser?.initials === 'ADM';

  // Auto-scroll for sync logs
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [syncLogs]);

  const handleSaveApiConfig = () => {
    setApiConfig(apiEdit);
    alert("Lexware API Einstellungen gespeichert!");
  };

  const handleSaveDbConfig = () => {
    localStorage.setItem('grt_crm_db_config', JSON.stringify(dbConfig));
    setIsDbModalOpen(false);
    alert('Datenbank-Zugangsdaten erfolgreich gespeichert!');
  };

  const handleStartSync = async () => {
    clearSyncLogs();
    setIsSyncDebugOpen(true);
    await syncLexwareInvoices();
  };

  const readCsvSmart = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target.result;
        try {
          const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
          const text = utf8Decoder.decode(buffer);
          resolve(text);
          return;
        } catch (err) {
          console.debug("UTF-8 decoding failed, trying ANSI fallback:", err.message);
        }
        const ansiDecoder = new TextDecoder('windows-1252');
        let text = ansiDecoder.decode(buffer);
        resolve(text);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSalesImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readCsvSmart(file);
      const count = await importSalesData(text);
      alert(`Import abgeschlossen!\n\n${count} neue Datensätze hinzugefügt.`);
      e.target.value = null;
    } catch (err) {
      alert("Fehler beim Umsatz-Import: " + err.message);
    }
  };

  const handleCustomerImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readCsvSmart(file);
      const result = await importCustomerData(text);
      alert(`Stammdaten-Import erfolgreich!\n\n${result.customers} Kundenprofile aktualisiert\n${result.contacts} Kontakte verarbeitet`);
      e.target.value = null;
    } catch (err) {
      alert("Fehler beim Kunden-Import: " + err.message);
    }
  };

  const handleAddUser = () => {
    if (!isAdmin) return;
    if (!newUser.name || !newUser.initials) return;
    const users = [...(data.users || []), { id: Date.now(), ...newUser }];
    updateUsers(users);
    setNewUser({ name: '', initials: '' });
    setIsUserModalOpen(false);
  };

  const handleDeleteUser = (id) => {
    if (!isAdmin) return;
    if (window.confirm("Nutzer wirklich löschen?")) {
      updateUsers(data.users.filter(u => u.id !== id));
    }
  };

  const handleClearDatabase = async () => {
    if (!isAdmin) return;
    if (window.confirm("ACHTUNG: Möchten Sie die interne Datenbank wirklich löschen?")) {
      await clearStorage();
      window.location.reload();
    }
  };

  const userStats = useMemo(() => {
    if (!data.logs || !evalRange.from || !evalRange.to) return [];
    const statsMap = {};
    const rangeTo = endOfDay(new Date(evalRange.to));
    const filteredLogs = data.logs.filter(log => {
      const logDate = new Date(log.timestamp);
      return isWithinInterval(logDate, { start: evalRange.from, end: rangeTo });
    });
    filteredLogs.forEach(log => {
      let userKey = log.user;
      if (!statsMap[userKey]) statsMap[userKey] = { initials: userKey, totalMinutes: 0, emailCount: 0, viewCount: 0, lastLogin: null };
      const user = statsMap[userKey];
      if (log.type === 'LOGIN') user.lastLogin = new Date(log.timestamp);
      if (log.type === 'LOGOUT' && user.lastLogin) {
        const duration = differenceInMinutes(new Date(log.timestamp), user.lastLogin);
        if (duration > 0 && duration < 600) user.totalMinutes += duration;
        user.lastLogin = null;
      }
      if (log.type === 'EMAIL' || log.type === 'EMAIL_SENT') user.emailCount++;
      if (log.type === 'VIEW_CUSTOMER') user.viewCount++;
    });
    return Object.values(statsMap).sort((a, b) => b.viewCount - a.viewCount);
  }, [data.logs, evalRange]);

  return (
    <div className="space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">System-Einstellungen</h1>
        <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm">
          <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs">{currentUser?.initials}</div>
          <div className="text-left">
            <p className="text-xs font-bold text-gray-900">{currentUser?.name}</p>
            <button onClick={logout} className="text-[9px] text-red-500 font-black uppercase hover:underline flex items-center gap-1 cursor-pointer">
              <SafeIcon icon={FiLogOut} /> Abmelden
            </button>
          </div>
        </div>
      </div>

      {/* Lexware API Integration */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiSettings} className="text-blue-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">Lexware API Integration</h3>
          </div>
          <div className="flex items-center gap-2">
            {lexwareApiConfig.lastSync && (
              <span className="text-[10px] font-bold text-gray-400">Letzter Sync: {format(new Date(lexwareApiConfig.lastSync), 'dd.MM. HH:mm')}</span>
            )}
            <button 
              disabled={loading || !apiEdit.apiKey}
              onClick={handleStartSync} 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-100 disabled:opacity-50 cursor-pointer"
            >
              <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} /> Jetzt abgleichen
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiKey} /> Lexware API Key
            </label>
            <input 
              type="password" 
              value={apiEdit.apiKey} 
              onChange={e => setApiEdit({...apiEdit, apiKey: e.target.value})}
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-mono text-sm"
              placeholder="Bearer Token..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiCalendar} /> Rechnungen ab Datum
            </label>
            <input 
              type="date" 
              value={apiEdit.startDate} 
              onChange={e => setApiEdit({...apiEdit, startDate: e.target.value})}
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-bold text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiRefreshCw} /> Automatischer Sync
            </label>
            <button 
              onClick={() => setApiEdit({...apiEdit, autoSync: !apiEdit.autoSync})}
              className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border transition-all cursor-pointer ${apiEdit.autoSync ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-400'}`}
            >
              <span className="text-sm font-bold">{apiEdit.autoSync ? 'Aktiviert (bei Start)' : 'Deaktiviert'}</span>
              <SafeIcon icon={apiEdit.autoSync ? FiToggleRight : FiToggleLeft} className="text-xl" />
            </button>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
          <button 
            onClick={handleSaveApiConfig}
            className="px-6 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-black transition-all flex items-center gap-2 shadow-lg shadow-gray-200 cursor-pointer"
          >
            <SafeIcon icon={FiSave} /> API Einstellungen speichern
          </button>
        </div>
      </motion.div>

      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Nutzerverwaltung */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="text-blue-600 text-xl" />
              <h3 className="text-lg font-bold text-gray-900">Nutzerverwaltung</h3>
            </div>
            {isAdmin && (
              <button onClick={() => setIsUserModalOpen(true)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
                <SafeIcon icon={FiUserPlus} />
              </button>
            )}
          </div>
          <div className="space-y-3">
            {data.users?.map(user => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center font-black text-blue-600">{user.initials}</div>
                  <div><p className="text-sm font-bold text-gray-900">{user.name}</p><p className="text-[10px] text-gray-400 font-bold">Kürzel: {user.initials}</p></div>
                </div>
                {isAdmin && user.initials !== 'ADM' && (
                  <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"><SafeIcon icon={FiTrash2} /></button>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Protokoll */}
        {isAdmin && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col max-h-[400px]">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2"><SafeIcon icon={FiActivity} className="text-orange-500 text-xl" /><h3 className="text-lg font-bold text-gray-900">Protokoll</h3></div>
              <div className="flex items-center gap-2">
                <button onClick={() => setIsEvaluationOpen(true)} className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-gray-800 flex items-center gap-2 transition-all shadow-lg cursor-pointer"><SafeIcon icon={FiPieChart} /> Auswertung</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
              {data.logs?.map(log => (
                <div key={log.id} className="text-[11px] p-2 bg-gray-50 rounded border-l-2 border-blue-500 flex justify-between items-start gap-4">
                  <div><p className="text-gray-600 leading-tight"><span className="font-black text-blue-600">{log.user}:</span> {log.details}</p></div>
                  <span className="text-[9px] text-gray-400 whitespace-nowrap font-mono">{format(new Date(log.timestamp), 'HH:mm')}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Data Management Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${isAdmin ? 'lg:col-span-2' : ''}`}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2"><SafeIcon icon={FiHardDrive} className="text-blue-600 text-xl" /><h3 className="text-lg font-bold text-gray-900">Daten-Management</h3></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="p-4 bg-green-50 border border-green-100 rounded-xl flex flex-col justify-between h-full">
              <div><span className="text-xs font-black text-green-400 uppercase tracking-wider">Umsatz</span><p className="text-sm font-bold text-green-900 mt-1">Lexware CSV</p></div>
              <button onClick={() => salesInputRef.current.click()} className="mt-4 w-full py-2 bg-white border border-green-200 text-green-700 rounded-lg text-xs font-bold hover:bg-green-100 flex items-center justify-center gap-2 cursor-pointer shadow-sm"><SafeIcon icon={FiTrendingUp} /> CSV laden</button>
              <input type="file" ref={salesInputRef} className="hidden" accept=".csv" onChange={handleSalesImport} />
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex flex-col justify-between h-full">
              <div><span className="text-xs font-black text-blue-400 uppercase tracking-wider">Stammdaten</span><p className="text-sm font-bold text-blue-900 mt-1">Kunden & Kontakte</p></div>
              <button onClick={() => customerInputRef.current.click()} className="mt-4 w-full py-2 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-50 flex items-center justify-center gap-2 cursor-pointer shadow-sm"><SafeIcon icon={FiUsers} /> CSV laden</button>
              <input type="file" ref={customerInputRef} className="hidden" accept=".csv" onChange={handleCustomerImport} />
            </div>
            
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex flex-col justify-between h-full">
              <div><span className="text-xs font-black text-gray-400 uppercase tracking-wider">Sicherung</span><p className="text-sm font-bold text-gray-900 mt-1">Lokales Backup</p></div>
              <div className="mt-4 flex flex-col gap-2">
                <button onClick={() => backupInputRef.current.click()} className="w-full py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold hover:bg-gray-50 flex items-center justify-center gap-2 cursor-pointer"><SafeIcon icon={FiUpload} /> laden</button>
                <button onClick={async () => {
                  const dataObj = await exportDatabase();
                  const blob = new Blob([JSON.stringify(dataObj, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `CRM_BACKUP_${format(new Date(), 'yyyy-MM-dd')}.json`; a.click();
                }} className="w-full py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-black flex items-center justify-center gap-2 cursor-pointer"><SafeIcon icon={FiDownload} /> Speichern</button>
              </div>
              <input type="file" ref={backupInputRef} className="hidden" accept=".json" onChange={async (e) => {
                const file = e.target.files[0]; if (!file) return;
                try {
                  const text = await file.text(); 
                  await restoreBackup(JSON.parse(text)); 
                  alert("Wiederherstellung abgeschlossen!");
                } catch (err) {
                  alert("Fehler beim Laden des Backups: " + err.message);
                }
              }} />
            </div>
            
            <div className="p-4 bg-orange-50 border border-orange-100 rounded-xl flex flex-col justify-between h-full">
              <div><span className="text-xs font-black text-orange-400 uppercase tracking-wider">Cloud</span><p className="text-sm font-bold text-orange-900 mt-1">MinIO Sync</p></div>
              <div className="mt-4 flex flex-col gap-2">
                <button disabled={loading} onClick={() => loadFromCloud()} className="w-full py-2 bg-white border border-orange-200 text-orange-700 rounded-lg text-xs font-bold hover:bg-orange-100 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"><SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} /> Cloud laden</button>
                <button disabled={loading} onClick={() => saveToCloud()} className="w-full py-2 bg-orange-600 text-white rounded-lg text-xs font-bold hover:bg-orange-700 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"><SafeIcon icon={FiCloud} /> Cloud sichern</button>
              </div>
            </div>

            <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl flex flex-col justify-between h-full">
              <div><span className="text-xs font-black text-purple-400 uppercase tracking-wider">Datenbank</span><p className="text-sm font-bold text-purple-900 mt-1">PostgreSQL Setup</p></div>
              <button onClick={() => setIsDbModalOpen(true)} className="mt-4 w-full py-2 bg-white border border-purple-200 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-100 flex items-center justify-center gap-2 cursor-pointer shadow-sm"><SafeIcon icon={FiDatabase} /> Konfigurieren</button>
            </div>
          </div>
          {isAdmin && (
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
              <button onClick={handleClearDatabase} className="py-2 px-4 text-xs text-red-500 font-bold hover:bg-red-50 rounded-lg border border-red-50 flex items-center justify-center gap-2 cursor-pointer"><SafeIcon icon={FiTrash2} /> Datenbank löschen</button>
            </div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {isEvaluationOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-[2rem] shadow-2xl w-full max-w-3xl flex flex-col overflow-visible">
              <div className="p-8 border-b border-gray-100 flex items-center justify-between">
                <div><h3 className="text-2xl font-black text-gray-900">Nutzungsprotokoll</h3><p className="text-gray-500 text-sm">Zusammenfassung der Nutzeraktivitäten</p></div>
                <button onClick={() => setIsEvaluationOpen(false)} className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-8 space-y-8 overflow-visible">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 overflow-visible">
                  <span className="text-sm font-bold text-gray-600">Zeitraum wählen:</span>
                  <CustomDatePicker selectedDateRange={evalRange} onChange={setEvalRange} />
                </div>
                <div className="border border-gray-200 rounded-2xl overflow-hidden">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] font-black tracking-widest border-b">
                      <tr><th className="px-6 py-4">Nutzer</th><th className="px-6 py-4">Dauer</th><th className="px-6 py-4 text-center">E-Mails</th><th className="px-6 py-4 text-center">Profilaufrufe</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {userStats.length > 0 ? userStats.map(stat => (
                        <tr key={stat.initials} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs">{stat.initials}</div><span className="font-bold text-gray-900">{stat.initials}</span></div></td>
                          <td className="px-6 py-4"><div className="flex items-center gap-2 font-medium text-gray-700"><SafeIcon icon={FiClock} className="text-blue-500" />{Math.floor(stat.totalMinutes/60)}h {stat.totalMinutes%60}m</div></td>
                          <td className="px-6 py-4 text-center"><span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full font-bold text-xs">{stat.emailCount}</span></td>
                          <td className="px-6 py-4 text-center"><span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full font-bold text-xs">{stat.viewCount}</span></td>
                        </tr>
                      )) : <tr><td colSpan={4} className="px-6 py-12 text-center text-gray-400 font-bold italic">Keine Aktivitäten</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end rounded-b-[2rem]"><button onClick={() => setIsEvaluationOpen(false)} className="px-8 py-3 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-gray-800 cursor-pointer">Fertig</button></div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sync Debug Modal */}
      <AnimatePresence>
        {isSyncDebugOpen && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[#0D1117] rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden border border-gray-800">
              <div className="p-6 border-b border-gray-800 flex items-center justify-between bg-[#161B22]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <SafeIcon icon={FiTerminal} className="text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Lexware Sync Debug</h3>
                    <p className="text-gray-400 text-xs">Echtzeit-Protokoll der Datenübertragung</p>
                  </div>
                </div>
                <button 
                  disabled={loading}
                  onClick={() => setIsSyncDebugOpen(false)} 
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 disabled:opacity-30 cursor-pointer"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              
              <div className="flex-1 p-6 bg-[#0D1117] min-h-[300px] max-h-[500px] overflow-y-auto font-mono text-xs custom-scrollbar">
                <div className="space-y-3">
                  {syncLogs.length === 0 && (
                    <div className="flex items-center gap-2 text-gray-500 italic">
                      <SafeIcon icon={FiInfo} className="animate-pulse" /> Warte auf Start...
                    </div>
                  )}
                  {syncLogs.map((log) => (
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }} 
                      animate={{ opacity: 1, x: 0 }} 
                      key={log.id} 
                      className="flex gap-4 group"
                    >
                      <span className="text-gray-600 whitespace-nowrap">{format(new Date(log.timestamp), 'HH:mm:ss.SSS')}</span>
                      <div className="flex items-start gap-2">
                        {log.type === 'success' && <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-0.5" />}
                        {log.type === 'error' && <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5" />}
                        {log.type === 'info' && <div className="w-1 h-1 bg-blue-400 rounded-full mt-1.5" />}
                        <span className={`
                          ${log.type === 'success' ? 'text-green-400 font-bold' : ''}
                          ${log.type === 'error' ? 'text-red-400 font-bold underline' : ''}
                          ${log.type === 'info' ? 'text-gray-300' : ''}
                        `}>
                          {log.message}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </div>

              <div className="p-4 border-t border-gray-800 bg-[#161B22] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {loading && (
                    <div className="flex items-center gap-2 text-blue-400 text-[10px] font-bold uppercase tracking-wider">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping" />
                      Übertragung aktiv
                    </div>
                  )}
                  {!loading && syncLogs.length > 0 && (
                    <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">
                      Übertragung beendet
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => setIsSyncDebugOpen(false)} 
                  className={`px-6 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${loading ? 'bg-gray-800 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/20'}`}
                >
                  {loading ? 'Warten...' : 'Schließen'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isUserModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 overflow-hidden">
              <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold">Neuer Nutzer</h3><button onClick={() => setIsUserModalOpen(false)} className="cursor-pointer"><SafeIcon icon={FiX} /></button></div>
              <div className="space-y-4">
                <div><label className="text-[10px] font-black text-gray-400 uppercase">Name</label><input type="text" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none" /></div>
                <div><label className="text-[10px] font-black text-gray-400 uppercase">Kürzel</label><input type="text" maxLength={3} value={newUser.initials} onChange={e => setNewUser({ ...newUser, initials: e.target.value.toUpperCase() })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none font-bold" /></div>
                <button onClick={handleAddUser} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg mt-4 cursor-pointer">Nutzer anlegen</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {/* Database Credentials Modal */}
      <AnimatePresence>
        {isDbModalOpen && (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 overflow-hidden">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <SafeIcon icon={FiDatabase} className="text-purple-600" /> Datenbank-Zugang
                </h3>
                <button onClick={() => setIsDbModalOpen(false)} className="cursor-pointer">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase">Containername</label>
                  <input type="text" value={dbConfig.container} onChange={e => setDbConfig({ ...dbConfig, container: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none text-sm font-medium" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase">Datenbankname</label>
                  <input type="text" value={dbConfig.dbName} onChange={e => setDbConfig({ ...dbConfig, dbName: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none text-sm font-medium" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase">Benutzername</label>
                  <input type="text" value={dbConfig.username} onChange={e => setDbConfig({ ...dbConfig, username: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none text-sm font-medium" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase">Passwort</label>
                  <input type="password" value={dbConfig.password} onChange={e => setDbConfig({ ...dbConfig, password: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase">Connection URL</label>
                  <input type="text" value={dbConfig.url} onChange={e => setDbConfig({ ...dbConfig, url: e.target.value })} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg mt-1 outline-none text-xs font-mono" />
                </div>
                
                <div className="pt-2">
                  <div className="p-3 bg-blue-50 text-blue-800 text-xs rounded-lg flex gap-2 items-start mb-4 border border-blue-100">
                    <SafeIcon icon={FiInfo} className="mt-0.5 flex-shrink-0" />
                    <p>Diese Zugangsdaten werden für die spätere Migration auf den VPS lokal gespeichert.</p>
                  </div>
                  <button onClick={handleSaveDbConfig} className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold shadow-lg transition-colors cursor-pointer flex justify-center items-center gap-2">
                    <SafeIcon icon={FiSave} /> Zugangsdaten speichern
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Settings;